import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import './LoginPage.css'


const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);

  // MFA step state
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaMethod, setMfaMethod] = useState("email");
  const [mfaMasked, setMfaMasked] = useState("");
  const mfaInputRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/auth/sso/status`)
      .then((r) => r.json())
      .then((d) => { if (d.enabled) setSsoEnabled(true); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("pm_session_expired") === "1") {
        setInfo("Your session has expired. Please sign in again.");
        sessionStorage.removeItem("pm_session_expired");
      }
      const params = new URLSearchParams(window.location.search);
      const ssoError = params.get("sso_error");
      if (ssoError) {
        const msgs = {
          not_configured:  "SSO is not configured. Please contact your administrator.",
          init_failed:     "Failed to initiate SSO sign-in. Please try again.",
          invalid_state:   "SSO sign-in failed — invalid request state. Please try again.",
          sso_disabled:    "SSO has been disabled. Please use email and password.",
          invalid_token:   "SSO sign-in failed — invalid token from identity provider.",
          no_email:        "Your Microsoft account did not provide an email address.",
          not_provisioned: "Your account is not provisioned for this platform. Contact your administrator.",
          callback_failed: "SSO sign-in failed. Please try again or contact support.",
        };
        setError(msgs[ssoError] || `SSO error: ${ssoError}`);
        window.history.replaceState(null, "", window.location.pathname);
      }
    } catch {}
  }, []);

  // Auto-focus the OTP input when the MFA step appears
  useEffect(() => {
    if (mfaStep) mfaInputRef.current?.focus();
  }, [mfaStep]);

  const handleSsoLogin = () => {
    setSsoLoading(true);
    window.location.href = `${API_BASE_URL}/api/auth/sso/login`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.message || "Login failed. Please try again.");
        setLoading(false);
        return;
      }

      if (data.mfaRequired) {
        setMfaToken(data.mfaSessionToken);
        setMfaMethod(data.mfaMethod || "email");
        setMfaMasked(data.mfaMaskedTarget || "");
        setMfaStep(true);
        setLoading(false);
        return;
      }

      login(data.token, data.user);
      if (data.user?.mustChangePassword) {
        navigate("/change-password", { replace: true });
        return;
      }
      navigate("/", { replace: true });
    } catch (err) {
      console.error("Login error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/verify-mfa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfaSessionToken: mfaToken, code: mfaCode }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.message || "Invalid code. Please try again.");
        setLoading(false);
        return;
      }

      login(data.token, data.user);
      if (data.user?.mustChangePassword) {
        navigate("/change-password", { replace: true });
        return;
      }
      navigate("/", { replace: true });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaStep(false);
    setMfaToken("");
    setMfaCode("");
    setError("");
  };

  const methodLabel = mfaMethod === "sms" ? "text message" : mfaMethod === "both" ? "email and text message" : "email";

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <img
            src="/Accenture-Emblem.png"
            alt="Accenture"
            className="login-brand-logo"
          />
          <div className="login-brand-divider" aria-hidden="true" />
          <div className="login-brand-product">
            <span className="login-brand-name">PhishMatrix AI</span>
            <span className="login-brand-tag">Email Threat Analysis Platform</span>
          </div>
        </div>

        <div className="login-accent-strip" aria-hidden="true" />

        {!mfaStep ? (
          <>
            <h1 className="login-title">Sign in to your account</h1>
            <p className="login-subtitle">
              Use your BFUSA credentials to access the platform.
            </p>

            <form onSubmit={handleSubmit} className="login-form">
              <label className="login-label">
                Email
                <input
                  type="email"
                  className="login-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@accenture.com"
                  autoComplete="username"
                  required
                />
              </label>

              <label className="login-label">
                Password
                <input
                  type="password"
                  className="login-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="off"
                  required
                />
              </label>

              {info && !error && <div className="login-info">{info}</div>}
              {error && <div className="login-error">{error}</div>}

              <button type="submit" className="login-button" disabled={loading}>
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            {ssoEnabled && (
              <>
                <div className="login-sso-divider">
                  <span className="login-sso-divider-line" />
                  <span className="login-sso-divider-text">or</span>
                  <span className="login-sso-divider-line" />
                </div>
                <button
                  type="button"
                  className="login-sso-button"
                  onClick={handleSsoLogin}
                  disabled={ssoLoading}
                >
                  <svg className="login-sso-ms-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width="18" height="18">
                    <path fill="#f25022" d="M0 0h11v11H0z"/>
                    <path fill="#00a4ef" d="M12 0h11v11H12z"/>
                    <path fill="#7fba00" d="M0 12h11v11H0z"/>
                    <path fill="#ffb900" d="M12 12h11v11H12z"/>
                  </svg>
                  {ssoLoading ? "Redirecting…" : "Sign in with SSO"}
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <h1 className="login-title">Two-factor verification</h1>
            <p className="login-subtitle">
              We sent a 6-digit code to your {methodLabel}
              {mfaMasked ? <> (<strong>{mfaMasked}</strong>)</> : ""}.
              Enter it below to continue.
            </p>

            <form onSubmit={handleMfaSubmit} className="login-form">
              <label className="login-label">
                Verification code
                <input
                  ref={mfaInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  className="login-input login-otp-input"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  required
                />
              </label>

              {error && <div className="login-error">{error}</div>}

              <button
                type="submit"
                className="login-button"
                disabled={loading || mfaCode.length < 6}
              >
                {loading ? "Verifying..." : "Verify"}
              </button>
            </form>

            <button
              type="button"
              className="login-back-link"
              onClick={handleBackToLogin}
            >
              ← Back to sign in
            </button>
          </>
        )}

        <div className="login-footer">
          <p className="login-notice">
            Access is restricted to authorized personnel. All activity is monitored
            and logged in accordance with corporate security policy.
          </p>
          <p className="login-notice">
            By signing in, you agree to the platform&rsquo;s Acceptable Use Policy
            and acknowledge that unauthorized access is prohibited and may be subject
            to civil and criminal penalties.
          </p>
          <p className="login-copyright">
            &copy; {new Date().getFullYear()} Accenture. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
